import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';

const STORAGE_KEY = 'module_load_retry_count';
const MAX_RETRIES = 3;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isReloading: boolean;
}

function isModuleLoadErrorStatic(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message || '';
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Unable to preload CSS') ||
    message.includes('Failed to load module script') ||
    message.includes('NetworkError')
  );
}

function getRetryCount(): number {
  try {
    return parseInt(sessionStorage.getItem(STORAGE_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function incrementRetryCount(): void {
  try {
    const count = getRetryCount();
    sessionStorage.setItem(STORAGE_KEY, String(count + 1));
  } catch {
    // sessionStorage недоступен, продолжаем без счётчика
  }
}

function resetRetryCount(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage недоступен
  }
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isReloading: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isModuleError = isModuleLoadErrorStatic(error);
    const retryCount = getRetryCount();

    if (isModuleError && retryCount < MAX_RETRIES) {
      return {
        hasError: false,
        error,
        errorInfo: null,
        isReloading: true,
      };
    }

    return {
      hasError: true,
      error,
      errorInfo: null,
      isReloading: false,
    };
  }

  componentDidMount() {
    resetRetryCount();
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    
    const isModuleError = isModuleLoadErrorStatic(error);
    const retryCount = getRetryCount();

    if (isModuleError) {
      if (retryCount < MAX_RETRIES) {
        console.warn(`[ErrorBoundary] Module load error. Retry ${retryCount + 1}/${MAX_RETRIES}. Reloading...`);
        incrementRetryCount();
        window.location.reload();
        return;
      } else {
        console.error(`[ErrorBoundary] Module load error. Max retries (${MAX_RETRIES}) reached.`);
        resetRetryCount();
        this.setState({ hasError: true, error, errorInfo, isReloading: false });
      }
    } else {
      this.setState({ error, errorInfo });
    }
  }

  handleReload = () => {
    resetRetryCount();
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo, isReloading } = this.state;

    if (isReloading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 800 }}>
            <Typography variant="h5" gutterBottom>
              Произошла ошибка
            </Typography>
            <Typography variant="body1" paragraph>
              При загрузке приложения произошла ошибка. Пожалуйста, попробуйте перезагрузить страницу.
            </Typography>
            {error && (
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace' }}>
                {error.toString()}
              </Typography>
            )}
            {errorInfo && (
              <Typography variant="body2" sx={{ mt: 2, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {errorInfo.componentStack}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReload}
              sx={{ mt: 2 }}
            >
              Перезагрузить страницу
            </Button>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

